// The Workflow page when `workflow_harness_enabled` is on: a run on the agent harness (./api.ts) instead of the engine.
// One run at a time: the question, the plan as the model writes it with an Approve button, the run's question as an
// ask card, the execution streamed, then the answer with its outcome and number audit and any drafted actions.
// Layout and styles are the engine page's (../v4/workflow-engine.css); nothing of the engine's run state is used.
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Info } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from '@/i18n';
import AppSidebar from '@/components/AppSidebar';
import { PAGE_HEADER, PAGE_TITLE } from '@/components/layout/pageHeader';
import { harnessApi, RUN_PARAM, working, type HarnessAsk, type HarnessRun } from './api';
import '../v4/workflow-engine.css';
import './harness.css';

// Every status is an ordinary workflow state, so its chip is neutral; only the outcome carries warning or error colours
// (AGENTS.md "Workflow status colors"): partial is a warning, blocked an error.
const OUTCOME_CLASS: Record<string, string> = { satisfied: 'ok', partial: 'warn', blocked: 'bad', cancelled: '' };



function Markdown({ text }: { text: string }) {
  return <div className="md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown></div>;
}

function AskCard({ ask, onAnswer }: { ask: HarnessAsk; onAnswer: (answer: string) => Promise<void> }) {
  const [answer, setAnswer] = useState(ask.safe_default ? (ask.options.find((o) => o.value === ask.safe_default)?.label ?? ask.safe_default) : '');
  const [sending, setSending] = useState(false);
  const send = () => {
    setSending(true);
    void onAnswer(answer).finally(() => setSending(false));
  };
  return (
    <div className="ask harness-ask" role="group" aria-label="Question from the run">
      <b className="harness-ask-title"><Info aria-hidden className="size-4" />{ask.question}</b>
      <div className="note">{ask.reason}</div>
      {ask.attempted.length > 0 && <div className="note">Checked: {ask.attempted.join('; ')}</div>}
      {ask.options.length > 0 && (
        <div className="chips">
          {ask.options.map((o) => (
            <button key={o.value} type="button" className={answer === o.label ? 'chip on' : 'chip'} onClick={() => setAnswer(o.label)}>
              {o.label}
              {o.value === ask.safe_default ? ' (default)' : ''}
            </button>
          ))}
        </div>
      )}
      <div className="bar">
        <input className="grow" aria-label="Your answer" value={answer} onChange={(e) => setAnswer(e.target.value)} />
        <button type="button" className="p" disabled={sending || !answer.trim()} onClick={send}>
          {sending ? 'Sending…' : 'Answer'}
        </button>
      </div>
    </div>
  );
}

export function HarnessRunView({ run, live, steps, act }: {
  run: HarnessRun;
  live: string;
  steps: string[];
  act: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  return (
    <>
      <div className="bar">
        <b className="grow">{run.question}</b>
        <span className="chip">{run.status.replace(/_/g, ' ')}</span>
        {run.outcome && <span className={`chip ${OUTCOME_CLASS[run.outcome] ?? ''}`}>{run.outcome}</span>}
        {run.model && <span className="chip">{run.model}{run.effort ? ` · ${run.effort}` : ''}</span>}
        <button type="button" disabled={run.status === 'finished'} onClick={() => void act(() => harnessApi.cancel(run.runId))}>
          Cancel
        </button>
      </div>
      {run.error && <div role="alert" className="bar err">{run.error}</div>}
      <div className="col">
        {run.plan && (
          <section aria-label="Plan">
            <div className="h">Plan</div>
            <Markdown text={run.plan} />
            {run.status === 'awaiting_approval' && (
              <button type="button" className="p" onClick={() => void act(() => harnessApi.approve(run.runId))}>
                Approve and run
              </button>
            )}
          </section>
        )}
        {run.openAsk && run.status === 'awaiting_ask' && (
          <AskCard key={run.openAsk.resume_token} ask={run.openAsk} onAnswer={(answer) => act(() => harnessApi.answer(run.openAsk!.resume_token, answer))} />
        )}
        {working(run) && (
          <section aria-label="Working">
            <div className="h">{run.phase === 'plan' ? 'Planning…' : 'Running…'}</div>
            {steps.length > 0 && <div className="note">{steps.length} steps: {steps.slice(-5).join(', ')}</div>}
            {live && <Markdown text={live} />}
          </section>
        )}
        {run.answer && (
          <section aria-label="Answer">
            <div className="h">Answer</div>
            <Markdown text={run.answer} />
            {run.audit && (
              <div className={run.audit.untraced.length ? 'note err' : 'note'}>
                {run.audit.untraced.length
                  ? `Numbers not found in any tool result: ${run.audit.untraced.join(', ')}`
                  : `Every number traced to a tool result (${run.audit.checked} checked, ${run.audit.toolCalls} tool calls).`}
              </div>
            )}
          </section>
        )}
        {run.actions.length > 0 && (
          <section aria-label="Drafted actions">
            <div className="h">Drafted actions (not sent)</div>
            {run.actions.map((a) => (
              <details key={a.action_id}>
                <summary>{a.kind === 'alert' ? 'Alert' : 'Work order'}: {a.title}</summary>
                <Markdown text={a.body} />
              </details>
            ))}
          </section>
        )}
      </div>
    </>
  );
}

export default function HarnessWorkflowPage() {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const [params, setParams] = useSearchParams();
  const runId = params.get(RUN_PARAM);
  const [run, setRun] = useState<HarnessRun | null>(null);
  const [live, setLive] = useState('');
  const [steps, setSteps] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!runId) return;
    let stopped = false;
    // Reloads overlap (the first load, then one per `state` frame): only a response newer than the last one applied
    // is shown, so a slow earlier answer never takes the run back a step.
    let requested = 0;
    let applied = 0;
    const reload = () => {
      const mine = ++requested;
      return harnessApi.run(runId).then((next) => {
        if (stopped || mine < applied) return;
        applied = mine;
        setRun(next);
        // A turn ended: its streamed text is now the plan, the ask or the answer.
        if (!working(next)) {
          setLive('');
          setSteps([]);
        }
      }, (e: Error) => setError(e.message));
    };
    void reload();
    const stop = harnessApi.watch(runId, (frame) => {
      if (frame.type === 'text') setLive((text) => text + frame.delta);
      else if (frame.type === 'step') setSteps((list) => [...list, frame.tool]);
      else if (frame.type === 'state') void reload();
    });
    return () => {
      stopped = true;
      stop();
    };
  }, [runId]);

  const act = (fn: () => Promise<unknown>) => {
    setError('');
    return fn().then(() => undefined).catch((e: Error) => setError(e.message));
  };
  const start = (ev: FormEvent) => {
    ev.preventDefault();
    if (busy || !question.trim()) return;
    // A run waiting on the user is not replaced without asking. The run library (reopening any earlier run) is week two.
    if (run && (run.status === 'awaiting_approval' || run.status === 'awaiting_ask') &&
      !window.confirm('The current run is waiting for you. Start a new one in its place?')) return;
    setBusy(true);
    // The current run stays on screen until its replacement has started; a failed start leaves it where it was.
    void act(() =>
      harnessApi.start(question).then(({ runId: id }) => {
        setRun(null);
        setLive('');
        setSteps([]);
        setParams((current) => {
          const next = new URLSearchParams(current);
          next.set(RUN_PARAM, id);
          return next;
        });
      }),
    ).finally(() => setBusy(false));
  };

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className={PAGE_HEADER}>
          <span className={PAGE_TITLE}>{t('nav:menu.workflow', { defaultValue: 'Workflow' })}</span>
        </header>
        <div className="wf-engine flex-1 overflow-y-auto">
          <main>
            <form className="bar" onSubmit={start}>
              <textarea className="grow" rows={2} aria-label="Your question" value={question} placeholder="Describe the workflow…"
                onChange={(e) => setQuestion(e.target.value)} />
              <button className="p" disabled={busy || !question.trim()}>{busy ? 'Starting…' : 'Plan'}</button>
            </form>
            {error && <div role="alert" className="bar err">{error}</div>}
            {run ? (
              <HarnessRunView run={run} live={live} steps={steps} act={act} />
            ) : (
              <div className="col">
                <span className="note">Describe the workflow. The agent writes a plan first; approve it to run it.</span>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
